import { BaseScene } from './BaseScene';
import { GamePlayManager } from '../managers/GamePlayManager';
import { PoolManager } from '../utils/ObjectPool';

export class GamePlayScene extends BaseScene {
  private gamePlayManager: GamePlayManager;
  private poolManager: PoolManager;
  private gameSpeedMultiplier: number = 2.0; // 2x game speed
  
  constructor() {
    super({ key: 'GamePlay' });
  }

  override create(): void {
    super.create();
    
    // Initialize object pools for performance
    this.poolManager = PoolManager.getInstance();
    this.poolManager.initializeGamePools(this);
    
    // Initialize the main game manager
    this.gamePlayManager = new GamePlayManager(this);
    
    // Add background with faster animations
    this.addFastBackground();
    
    // Initialize game through manager
    this.gamePlayManager.initializeGame();
    
    // Setup input handlers
    this.setupGameplayInputHandlers();
    
    // Set fast physics
    this.physics.world.timeScale = this.gameSpeedMultiplier;
  }

  private setupGameplayInputHandlers(): void {
    // Handle clicks for tower placement
    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      // Delegate to game manager for tower placement
      this.gamePlayManager.placeTowerAt(pointer.x, pointer.y);
    });
    
    // Handle keyboard inputs
    this.input.keyboard?.on('keydown-ESC', () => {
      // Show pause menu or settings
      // this.scene.pause();
    });
    
    this.input.keyboard?.on('keydown-SPACE', () => {
      // Try to activate Earth ability
      this.gamePlayManager.tryActivateEarthAbility();
    });
  }

  private addFastBackground(): void {
    const width = this.cameras.main.width;
    const height = this.cameras.main.height;
    
    // Đơn giản hóa: chỉ 1 lớp gradient tối
    const spaceBg = this.add.graphics();
    spaceBg.fillGradientStyle(0x101522, 0x1a2233, 0x16213e, 0x101522, 1);
    spaceBg.fillRect(0, 0, width, height);
    spaceBg.setDepth(-1000);
    
    // Starfield: chỉ 1 lớp, ít sao, alpha thấp, KHÔNG ANIMATION
    this.createStaticStarfield();
    
    // Nebula: chỉ 1 cloud lớn, alpha thấp, màu dịu, KHÔNG ANIMATION
    this.createStaticNebula();
  }

  private createStaticStarfield(): void {
    const width = this.cameras.main.width;
    const height = this.cameras.main.height;
    const starLayer = this.add.graphics();
    starLayer.setDepth(-900);
    const starCount = 30;
    for (let i = 0; i < starCount; i++) {
      const x = Math.random() * width;
      const y = Math.random() * height;
      const size = 0.7 + Math.random() * 0.7;
      const alpha = 0.10 + Math.random() * 0.10;
      starLayer.fillStyle(0xffffff, alpha);
      starLayer.fillCircle(x, y, size);
    }
    // KHÔNG ANIMATION
  }

  private createStaticNebula(): void {
    const width = this.cameras.main.width;
    const height = this.cameras.main.height;
    const cloud = this.add.graphics();
    // Màu tím-xanh dịu, alpha thấp
    cloud.fillStyle(0x3a4a6b, 0.08);
    cloud.fillEllipse(width * 0.5, height * 0.4, width * 0.8, height * 0.3);
    cloud.setDepth(-800);
    // KHÔNG ANIMATION
  }

  override update(time: number, delta: number): void {
    super.update(time, delta);
    
    // Apply speed multiplier to delta for faster game logic
    const fastDelta = delta * this.gameSpeedMultiplier;
    
    // Delegate all game logic to the manager with fast delta
    this.gamePlayManager.update(time, fastDelta);
    
    // Performance monitoring
    // if (time % 5000 < fastDelta) {
    //   this.poolManager.logPerformanceStats();
    // }
  }

  // Speed control methods
  public setGameSpeed(multiplier: number): void {
    this.gameSpeedMultiplier = multiplier;
    this.physics.world.timeScale = multiplier;
    
    // Update spawn manager speed
    const spawnManager = this.gamePlayManager.getSpawnManager();
    if (spawnManager) {
      spawnManager.setGameSpeed(multiplier);
    }
  }

  public getGameSpeed(): number {
    return this.gameSpeedMultiplier;
  }
  
  // Public callback methods for managers
  public startTowerPlacement(towerType: string): void {
    this.gamePlayManager.startTowerPlacement(towerType);
  }
  
  public tryActivateEarthAbility(): void {
    this.gamePlayManager.tryActivateEarthAbility();
  }

  public upgradeEarth(): void {
    this.gamePlayManager.upgradeEarth();
  }

  public upgradeTowerFromManager(tower: any): void {
    this.gamePlayManager.upgradeTowerFromManager(tower);
  }

  public sellTowerFromManager(tower: any): void {
    this.gamePlayManager.sellTowerFromManager(tower);
  }

  public upgradePetFromManager(pet: any): void {
    this.gamePlayManager.upgradePetFromManager(pet);
  }

  public spawnPet(petType: string): void {
    this.gamePlayManager.spawnPet(petType);
  }

  public watchAdForBoost(boostType: string): void {
    this.gamePlayManager.watchAdForBoost(boostType);
  }

  public addEnergyCredits(amount: number): void {
    this.gamePlayManager.addEnergyCredits(amount);
  }

  public reviveFromAd(): void {
    this.gamePlayManager.reviveFromAd();
  }

  // Utility methods for managers
  public addGold(amount: number): void {
    const userProfile = (window as any).userProfile;
    if (userProfile) {
      userProfile.addGold(amount);
    }
  }

  public addRuby(amount: number): void {
    const userProfile = (window as any).userProfile;
    if (userProfile) {
      userProfile.addRuby(amount);
    }
  }

  public addRandomEquipment(rarity: string): void {
    const userProfile = (window as any).userProfile;
    if (userProfile && userProfile.equipmentManager) {
      // This would be implemented by the equipment system
      console.log(`Adding random ${rarity} equipment`);
    }
  }

  // Override destroy to cleanup managers
  override destroy(): void {
    if (this.gamePlayManager) {
      // Cleanup managers if needed
    }
    super.destroy();
  }
} 